
import React, { useEffect, useState, useRef } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const SendNotification = () => {
  const [type, setType] = useState("Text");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [description, setDescription] = useState("");
  const [videoFile, setVideoFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [category, setCategory] = useState("all");
  const [audience, setAudience] = useState("all");
  const [role, setRole] = useState("vendor");
  const [categories, setCategories] = useState([]);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isAudienceOpen, setIsAudienceOpen] = useState(false);
  const [isRoleOpen, setIsRoleOpen] = useState(false);
  const [searchCategory, setSearchCategory] = useState("");
  const categoryDropdownRef = useRef(null);
  const audienceDropdownRef = useRef(null);
  const roleDropdownRef = useRef(null);
  const videoFileInputRef = useRef(null);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setIsCategoryOpen(false);
      }
      if (audienceDropdownRef.current && !audienceDropdownRef.current.contains(event.target)) {
        setIsAudienceOpen(false);
      }
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target)) {
        setIsRoleOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No authentication token found");

        const res = await fetch("https://visitguru.in/api/v1/category/getCategory", {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) {
          setCategories(data.data);
        } else {
          toast.error(data.message || "Failed to fetch categories ❌");
        }
      } catch (err) {
        console.error("❌ Error fetching categories:", err);
        toast.error("Error fetching categories. Please try again.");
      }
    };

    fetchCategories();
  }, []);

  // Reset file input when type changes to Text
  useEffect(() => {
    if (type === "Text" && videoFileInputRef.current) {
      videoFileInputRef.current.value = "";
      setVideoFile(null);
      setVideoUrl("");
    }
  }, [type]);

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("video/")) {
        toast.error("Please select a valid video file.");
        setVideoFile(null);
        if (videoFileInputRef.current) {
          videoFileInputRef.current.value = "";
        }
        return;
      }
      const maxSize = 100 * 1024 * 1024; // 100MB in bytes
      if (file.size > maxSize) {
        toast.error("Video file size exceeds 100MB limit.");
        setVideoFile(null);
        if (videoFileInputRef.current) {
          videoFileInputRef.current.value = "";
        }
        return;
      }
      setVideoFile(file);
      setVideoUrl(""); // Clear video URL when file is selected
    }
  };

  // Handle video URL change
  const handleVideoUrlChange = (e) => {
    setVideoUrl(e.target.value);
    setVideoFile(null); // Clear video file when URL is provided
    if (videoFileInputRef.current) {
      videoFileInputRef.current.value = "";
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();

    // Validate fields
    const missingFields = [];
    if (!title) missingFields.push("title");
    if (!message) missingFields.push("message");
    if (!category) missingFields.push("category");
    if (!audience) missingFields.push("audience");
    if (!role) missingFields.push("role");
    if (type === "Video" && !videoFile && !videoUrl) {
      missingFields.push("video file or URL");
    }

    if (missingFields.length > 0) {
      toast.error(`Please fill in: ${missingFields.join(", ")}`);
      return;
    }

    // Prepare payload
    const formData = new FormData();
    formData.append("title", title);
    formData.append("body", message);
    formData.append("description", description);
    formData.append("type", type);
    formData.append("audience", audience);
    formData.append("role", role);
    formData.append("categoryName", category === "all" ? "all" : categories.find((cat) => cat._id === category)?.categoryName || "");

    if (type === "Video") {
      if (videoFile) {
        formData.append("video", videoFile); // Backend expects file under "video"
      } else if (videoUrl) {
        formData.append("videoUrl", videoUrl);
      }
    }

    const url = "https://visitguru.in/api/v1/notification/vendor";

    try {
      toast.info("Sending notification...");
      const res = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: formData,
      });

      const data = await res.json();
      toast.dismiss();
      if (res.ok && data.success) {
        toast.success(data.message || "Notification sent successfully ✅");
        // Reset form
        setTitle("");
        setMessage("");
        setDescription("");
        setVideoFile(null);
        setVideoUrl("");
        setCategory("all");
        setAudience("all");
        setRole("vendor");
        setType("Text");
        setSearchCategory("");
        if (videoFileInputRef.current) {
          videoFileInputRef.current.value = "";
        }
      } else {
        toast.error(data.message || "Failed to send notification ❌");
      }
    } catch (err) {
      toast.dismiss();
      console.error("❌ Notification error:", err);
      toast.error("Failed to send notification: " + err.message);
    }
  };

  // Category options
  const categoryOptions = [
    { value: "all", label: "All Categories" },
    ...categories.map((cat) => ({
      value: cat._id,
      label: cat.categoryName || "N/A",
    })),
  ];

  // Filter categories based on search input
  const filteredCategories = categoryOptions.filter((cat) =>
    cat.label.toLowerCase().includes(searchCategory.toLowerCase())
  );

  // Audience options
  const audienceOptions = [
    { value: "all", label: "All Users" },
    { value: "subscribed", label: "Subscribed Users" },
    { value: "nonsubscribed", label: "Non-Subscribed Users" },
  ];

  // Role options
  const roleOptions = [
    { value: "vendor", label: "Vendor" },
    { value: "employee", label: "Employee" },
    { value: "user", label: "User" },
  ];

  return (
    <div className="space-y-9 p-4">
      <ToastContainer position="top-right" autoClose={2000} hideProgressBar={false} />
      <div className="relative flex flex-col min-w-0 break-words bg-white w-full shadow-lg rounded">
        <div className="rounded-t px-4 py-3 border-0 bg-blue-600 text-black">
          <h2 className="font-semibold">🔔 Notification</h2>

        </div>

        <form onSubmit={handleSend} className="p-8 space-y-6 bg-gray-50">
          {/* Title */}
          <div>
            <label className="block mb-1 font-medium text-gray-700">Title</label>
            <input
              type="text"
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
              placeholder="Enter notification title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          {/* Message */}
          <div>
            <label className="block mb-1 font-medium text-gray-700">Message</label>
            <textarea
              rows={4}
              className="w-full p-2 border border-gray-300 rounded-md resize-none focus:ring-2 focus:ring-blue-500 outline-none"
              placeholder="Type your message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
            />
          </div>

          {/* Description */}
          {/* <div>
            <label className="block mb-1 font-medium text-gray-700">Description</label>
            <textarea
              rows={4}
              className="w-full p-2 border border-gray-300 rounded-md resize-none focus:ring-2 focus:ring-blue-500 outline-none"
              placeholder="Type your description..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              
            />
          </div> */}

          {/* Type Selector */}
          <div>
            <label className="block mb-1 font-medium text-gray-700">Notification Type</label>
            <select
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              <option value="Text">Text</option>
              <option value="Video">Video</option>
            </select>
          </div>

          {/* Video Inputs */}
          {type === "Video" && (
            <div className="space-y-4">
              <div>
                {/* <label className="block mb-1 font-medium text-gray-700">Select Video File</label> */}
                {/* <input
                  id="video-file"
                  type="file"
                  accept="video/*"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
                  onChange={handleFileChange}
                  ref={videoFileInputRef}
                /> */}
                {videoFile && (
                  <p className="mt-2 text-sm text-gray-600"></p>
                )}
              </div>
              <div>
                <label className="block mb-1 font-medium text-gray-700">Enter Video URL</label>
                <input
                  type="url"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="Enter video URL"
                  value={videoUrl}
                  onChange={handleVideoUrlChange}
                />
              </div>
            </div>
          )}

          {/* Category Dropdown */}
          <div className="relative" ref={categoryDropdownRef}>
            <label className="block mb-1 font-medium text-gray-700">Category</label>
            <div
              className="w-full p-2 border border-gray-300 rounded-md cursor-pointer text-sm"
              onClick={() => {
                setIsCategoryOpen((prev) => !prev);
                setIsAudienceOpen(false);
                setIsRoleOpen(false);
              }}
            >
              {categoryOptions.find((opt) => opt.value === category)?.label || "Select Category"}
            </div>
            {isCategoryOpen && (
              <div className="absolute z-10 bg-white border border-gray-300 mt-1 w-full rounded shadow-lg max-h-60 overflow-y-auto">
                <input
                  type="text"
                  placeholder="Search category..."
                  value={searchCategory}
                  onChange={(e) => setSearchCategory(e.target.value)}
                  className="w-full px-3 py-2 border-b border-gray-300 focus:outline-none text-sm"
                />
                {filteredCategories.length > 0 ? (
                  filteredCategories.map((cat) => (
                    <div
                      key={cat.value}
                      className="px-3 py-2 hover:bg-blue-100 cursor-pointer text-sm"
                      onClick={() => {
                        setCategory(cat.value);
                        setIsCategoryOpen(false);
                        setSearchCategory("");
                      }}
                    >
                      {cat.label}
                    </div>
                  ))
                ) : (
                  <div className="px-3 py-2 text-gray-500 text-sm">No categories found</div>
                )}
              </div>
            )}
          </div>

          {/* Audience Dropdown */}
          <div className="relative" ref={audienceDropdownRef}>
            <label className="block mb-1 font-medium text-gray-700">Audience</label>
            <div
              className="w-full p-2 border border-gray-300 rounded-md cursor-pointer text-sm"
              onClick={() => {
                setIsAudienceOpen((prev) => !prev);
                setIsCategoryOpen(false);
                setIsRoleOpen(false);
              }}
            >
              {audienceOptions.find((opt) => opt.value === audience)?.label || "Select Audience"}
            </div>
            {isAudienceOpen && (
              <div className="absolute z-10 bg-white border border-gray-300 mt-1 w-full rounded shadow-lg max-h-60 overflow-y-auto">
                {audienceOptions.map((opt) => (
                  <div
                    key={opt.value}
                    className="px-3 py-2 hover:bg-blue-100 cursor-pointer text-sm"
                    onClick={() => {
                      setAudience(opt.value);
                      setIsAudienceOpen(false);
                    }}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Role Dropdown */}
          <div className="relative" ref={roleDropdownRef}>
            <label className="block mb-1 font-medium text-gray-700">Target Role</label>
            <div
              className="w-full p-2 border border-gray-300 rounded-md cursor-pointer text-sm"
              onClick={() => {
                setIsRoleOpen((prev) => !prev);
                setIsCategoryOpen(false);
                setIsAudienceOpen(false);
              }}
            >
              {roleOptions.find((opt) => opt.value === role)?.label || "Select Role"}
            </div>
            {isRoleOpen && (
              <div className="absolute z-10 bg-white border border-gray-300 mt-1 w-full rounded shadow-lg max-h-60 overflow-y-auto">
                {roleOptions.map((opt) => (
                  <div
                    key={opt.value}
                    className="px-3 py-2 hover:bg-blue-100 cursor-pointer text-sm"
                    onClick={() => {
                      setRole(opt.value);
                      setIsRoleOpen(false);
                    }}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="pt-4">
            <button
              style={{
                backgroundColor: "#0884c4",
                color: "#fff",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
              }}
              type="submit"
              className="w-full h-10 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Send Notification
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SendNotification;
